import React, { FC, PropsWithChildren, useMemo } from "react";
import { Box, Image, View } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Text } from "@lookiero/sty-psp-ui";
import { useExtendedScreen } from "../../hooks/useExtendedScreen";
import { DOMAIN } from "../../i18n/i18n";
import { style as emptyStateBannerStyle } from "./EmptyStateBanner.style";

interface EmptyStateBannerProps {
  readonly imageUrl: string;
  readonly title: string;
  readonly subtitle: string;
  readonly cta: string;
  readonly onLayout?: () => void;
  readonly onPress: () => void;
}

const EmptyStateBanner: FC<PropsWithChildren<EmptyStateBannerProps>> = ({
  imageUrl,
  cta,
  subtitle,
  title,
  children,
  onLayout,
  onPress,
}) => {
  const screen = useExtendedScreen();
  const isDesktopScreen = screen.XL;
  const isTabletScreen = screen.L;
  const isExtraLargeScreen = isDesktopScreen && screen.height >= 1080;

  const titleText = useI18nMessage({ domain: DOMAIN, id: title });
  const subtitleText = useI18nMessage({ domain: DOMAIN, id: subtitle });
  const ctaText = useI18nMessage({ domain: DOMAIN, id: cta });

  const style = useMemo(() => emptyStateBannerStyle(), []);

  return (
    <View style={[style.wrapper, isDesktopScreen ? undefined : style.wrapperSmall]} onLayout={onLayout}>
      <Box style={[style.imageContainer, isDesktopScreen ? style.halfWidth : style.fullWidth]}>
        <Image resizeMode="cover" src={imageUrl} style={style.image} />
      </Box>
      <Box
        style={[
          style.infoContainer,
          isDesktopScreen ? style.halfWidth : style.fullWidth,
          isDesktopScreen ? style.infoContainerLarge : style.infoContainerSmall,
          isExtraLargeScreen ? style.infoContainerExtraLarge : undefined, // its a UX requirement
          isTabletScreen ? style.infoContainerMedium : undefined,
        ]}
      >
        <Text level={isDesktopScreen ? 2 : 3} style={style.title} variant="heading">
          {titleText}
        </Text>
        <Text level={3} style={style.description}>
          {subtitleText}
        </Text>
        {children}
        <Button
          size="small"
          style={isTabletScreen ? undefined : { button: style.button }}
          variant="secondary"
          onPress={onPress}
        >
          {ctaText}
        </Button>
      </Box>
    </View>
  );
};

export { EmptyStateBanner };
